/**
 * The payment-link token (payment recovery, /pay#t=<token>).
 *
 * The token is the only credential for a card payment, so this module is
 * the one place that ever holds it:
 *
 *   - it arrives in the URL FRAGMENT, which browsers send to no server and
 *     put in no Referer;
 *   - it is read once, at the very start of main.jsx — before telemetry,
 *     before any provider, before the router — and the fragment is removed
 *     at once, so it is not left in the address bar or in history;
 *   - it lives in module memory, plus sessionStorage (this tab only) so a
 *     reload of the tab keeps working;
 *   - it leaves this module only through getPayLinkToken(), whose single
 *     caller is api/payLinkApi.js, which puts it in a request header.
 *
 * Nothing here logs. Never pass the token to track(), a route, a query
 * string, an href, a data- attribute or a text node.
 */

const STORAGE_KEY = 'gc.pay.link';
const PAY_PATH = /^\/pay\/?$/;
// 32 random bytes, base64url (the API accepts 20–200 characters).
const TOKEN_SHAPE = /^[A-Za-z0-9_-]{20,200}$/;
const FRAGMENT = /^#t=([^&]*)/;

let memoryToken = null;

const validToken = (value) => (typeof value === 'string' && TOKEN_SHAPE.test(value) ? value : null);

function store() {
  try {
    return typeof window !== 'undefined' ? window.sessionStorage : null;
  } catch {
    return null; // storage disabled
  }
}

/** Is this document the payment page? Decided from the path alone. */
export function isPayPath(pathname) {
  const path = pathname ?? (typeof window !== 'undefined' ? window.location.pathname : '');
  return PAY_PATH.test(path || '');
}

/**
 * Read the token out of the fragment and remove the fragment. A token in
 * the fragment replaces a stored one (a newer link was opened in this tab);
 * with no fragment the tab's stored token is used (a reload).
 * Does nothing on any path other than /pay.
 */
export function capturePayLinkToken() {
  if (typeof window === 'undefined' || !isPayPath()) return;
  const { location, history } = window;
  const match = FRAGMENT.exec(location.hash || '');
  if (match) {
    // Remove it whether or not it is usable: nothing token-like stays in the URL.
    try {
      history.replaceState(history.state, '', `${location.pathname}${location.search}`);
    } catch { /* a browser that refuses still gets the page */ }
    let raw = match[1];
    try { raw = decodeURIComponent(raw); } catch { /* keep the raw text; it fails the shape check */ }
    const fromLink = validToken(raw);
    if (fromLink) {
      memoryToken = fromLink;
      try { store()?.setItem(STORAGE_KEY, fromLink); } catch { /* memory only */ }
      return;
    }
  }
  if (!memoryToken) {
    let kept = null;
    try { kept = store()?.getItem(STORAGE_KEY) ?? null; } catch { /* none */ }
    memoryToken = validToken(kept);
  }
}

/** The token, for the X-Pay-Link-Token header. Only api/payLinkApi.js calls this. */
export function getPayLinkToken() {
  return memoryToken;
}

export function hasPayLinkToken() {
  return memoryToken != null;
}

/** The payment is finished, or the link is dead: forget the token. */
export function clearPayLinkToken() {
  memoryToken = null;
  try { store()?.removeItem(STORAGE_KEY); } catch { /* nothing to remove */ }
}

/**
 * Ask the browser to send no Referer from this document (the 3DS frames
 * and any link on the page). The fragment is never part of a Referer; this
 * also keeps the bare /pay path to ourselves.
 */
export function applyNoReferrerPolicy() {
  if (typeof document === 'undefined') return;
  let meta = document.querySelector('meta[name="referrer"]');
  if (!meta) {
    meta = document.createElement('meta');
    meta.setAttribute('name', 'referrer');
    document.head.appendChild(meta);
  }
  meta.setAttribute('content', 'no-referrer');
}

// Runs when main.jsx imports this module — its first import — so the
// fragment is gone before any other module of the app is evaluated.
capturePayLinkToken();
if (isPayPath()) applyNoReferrerPolicy();
